import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AuthService, AuthMeResponse } from './auth.service';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { Charity } from '../../models/Charity';
import { Business } from '../../models/Business';
import { User } from '../../models/User';
import { ApiUrlService } from '../api/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class UserRolesService implements OnDestroy {
  private get authApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/auth`); }

  private readonly permittedCharitiesSubject = new BehaviorSubject<Charity[]>([]);
  private readonly permittedBusinessesSubject = new BehaviorSubject<Business[]>([]);
  private readonly rolesLoadedSubject = new BehaviorSubject<boolean>(false);

  readonly permittedCharities$ = this.permittedCharitiesSubject.asObservable();
  readonly permittedBusinesses$ = this.permittedBusinessesSubject.asObservable();
  readonly rolesLoaded$ = this.rolesLoadedSubject.asObservable();

  private readonly authService = inject(AuthService);
  private readonly httpClient = inject(HttpClientWrapperService);
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly userSubscription: Subscription;
  private loadSubscription: Subscription | null = null;

  constructor() {
    this.userSubscription = this.authService.user$.pipe(
      distinctUntilChanged((prev: User | null, curr: User | null) => prev?.id === curr?.id)
    ).subscribe((user: User | null) => {
      if (user) {
        this.loadUserRoles();
      } else {
        this.clearRoles();
      }
    });
  }

  private loadUserRoles(): void {
    this.loadSubscription?.unsubscribe();
    this.rolesLoadedSubject.next(false);

    // Reuses the session's cached /auth/me rather than issuing a second GET — AuthService has
    // already fetched it by the time user$ emits a user, which is what triggers this load.
    this.loadSubscription = this.authService.getAuthMe().pipe(
      switchMap((data: AuthMeResponse) => {
        const roles: string[] = [];

        if (data.authorities && data.authorities.length > 0) {
          roles.push(...data.authorities);
        }

        const hasCharity: boolean = roles.includes('ROLE_CHARITY');
        const hasBusiness: boolean = roles.includes('ROLE_BUSINESS');

        if (!hasCharity && !hasBusiness) {
          return of({ charities: [] as Charity[], businesses: [] as Business[] });
        }

        return forkJoin({
          charities: hasCharity
            ? this.httpClient.get<Charity[]>(this.apiUrlService.buildUrl(`/api/v1/permitted-charities`)).pipe(
                catchError(() => of([] as Charity[]))
              )
            : of([] as Charity[]),
          businesses: hasBusiness
            ? this.httpClient.get<Business[]>(this.apiUrlService.buildUrl(`/api/v1/permitted-businesses`)).pipe(
                catchError(() => of([] as Business[]))
              )
            : of([] as Business[]),
        });
      }),
      catchError(() => of({ charities: [] as Charity[], businesses: [] as Business[] }))
    ).subscribe({
      next: ({ charities, businesses }) => {
        this.permittedCharitiesSubject.next(charities || []);
        this.permittedBusinessesSubject.next(businesses || []);

        this.rolesLoadedSubject.next(true);
      },
      error: () => {
        this.rolesLoadedSubject.next(true);
      }
    });
  }

  private clearRoles(): void {
    this.loadSubscription?.unsubscribe();
    this.permittedCharitiesSubject.next([]);
    this.permittedBusinessesSubject.next([]);
    this.rolesLoadedSubject.next(false);
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.loadSubscription?.unsubscribe();
  }
}
